import React from 'react';
import * as Routes from '@/modules/routes';
import AIReadingLevelGenerating from '@/components/shared/AIReadingLevelGenerating';
import Icon from '@/components/ui/Icon';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import { GRADE_BAND_LABELS } from '@/components/shared/TextControlsToolbar/AIReadingLevelButton/constants';
import useReadingLevelGeneratingStore from '@/stores/useReadingLevelGeneratingStore';
import { COLORS } from '@/components/ui/ui.constants';
import { toast } from '@/components/ui/Toast';
import showLevelingStatusToast, { STATUS_TOAST_ID } from './showLevelingStatusToast';

export const FAILURE_TOAST_ID = 'ai-leveling-failure';

interface SectionAIGeneratingPanelProps {
  programId: number;
  sectionId: number;
}

const SuccessIcon = () => (
  <span
    className={cn(
      'tw-shrink-0 tw-flex tw-items-center tw-justify-center',
      'tw-w-5 tw-h-5 tw-rounded-full tw-bg-green',
    )}
  >
    <Icon color="white" icon="Checkmark" size={16} />
  </span>
);

const ErrorIcon = () => (
  <Icon className="tw-shrink-0" color={COLORS.red} icon="Warning" size={24} />
);

const SectionAIGeneratingPanel = ({ programId, sectionId }: SectionAIGeneratingPanelProps) => {
  const { clearGenerating, generating, markGenerated, setFailed, startGenerating } = useReadingLevelGeneratingStore();

  const buildUrl = (gradeBand: string) => {
    const inIframe = new URLSearchParams(window.location.search).get('in_iframe') === 'true';
    return Routes.shared_section_path(sectionId, {
      in_iframe: inIframe,
      program_id: programId,
      reading_level: gradeBand,
    });
  };

  const handleSuccess = () => {
    if (!generating) return;

    markGenerated(generating.sectionId, generating.gradeBand);
    clearGenerating();
    showLevelingStatusToast(<SuccessIcon />, I18n.t('ai_reading_level_ready'), {
      action: { href: buildUrl(generating.gradeBand), label: I18n.t('ai_reading_level_view') },
    });
  };

  const handleError = () => {
    if (!generating) return;

    const failedParams = { ...generating };
    setFailed(failedParams);
    clearGenerating();

    const gradeBandLabel = GRADE_BAND_LABELS[
      failedParams.gradeBand as keyof typeof GRADE_BAND_LABELS
    ];
    // Dismiss the in-progress toast and show the failure under its own id so it doesn't inherit
    // the in-progress toast's auto-close timer. autoClose defaults to false → persists until
    // dismissed, keeping the "Try again" action available (PLAT-17351).
    toast.dismiss(STATUS_TOAST_ID);
    showLevelingStatusToast(
      <ErrorIcon />,
      I18n.t('ai_reading_level_create_failed', { band: gradeBandLabel || failedParams.gradeBand }),
      {
        action: { label: I18n.t('ai_reading_level_try_again'), onClick: () => startGenerating(failedParams) },
        toastId: FAILURE_TOAST_ID,
      },
    );
  };

  if (!generating) return null;

  return (
    <AIReadingLevelGenerating
      generatePath={generating.generatePath}
      gradeBand={generating.gradeBand}
      programId={generating.programId ?? programId}
      sectionId={generating.sectionId}
      showLoadingSpinner={false}
      onError={handleError}
      onSuccess={handleSuccess}
    />
  );
};

export default SectionAIGeneratingPanel;
